import { ApolloError, DocumentNode, OperationVariables, gql } from '@apollo/client';

import { GraphQLResponsePaginationInfo } from 'modules/graphql/types';
import { isDefined } from 'utils/tsHelpers';
import noOp from 'utils/noop';
import { useGraphql } from 'utils/hooks/useGraphql';
import { GraphQLSnippet } from '../types';

export type FetchSnippetsQueryRequest = {
  pagination?: {
    limit: number;
    offset: number;
  };
  search?: string | null;
  sort?: {
    sortBy: string;
    sortDirection: string;
  };
};

export type UseSnippetsResponse = {
  errorMessage?: ApolloError;
  hasError?: boolean;
  isLoading: boolean;
  results: GraphQLSnippet[];
  paginationInfo: GraphQLResponsePaginationInfo;
  refetch: (variables?: Partial<FetchSnippetsQueryRequest>) => void;
};

export const fetchSnippetsQuery = gql`
  query FetchSnippets($pagination: Pagination, $search: String, $sort: Sort) {
    fetchSnippets(pagination: $pagination, search: $search, sort: $sort) {
      paginationInfo {
        count
        offset
        limit
        page
        __typename
      }
      results {
        id
        createdAt
        updatedAt
        updatedBy
        updatedByUser {
          id
          fullName
          avatarUrl
        }
        projectId
        name
        creatorUserId
        creatorUser {
          id
          fullName
          avatarUrl
        }
        content
        description
        positionalParameters
        __typename
      }
    }
  }
`;

/**
 * Raw respone from GQL.
 */
export type FetchSnippetQueryResponse = {
  fetchSnippets: {
    paginationInfo: GraphQLResponsePaginationInfo;
    results: GraphQLSnippet[];
  };
};

/**
 * A fn to
 * @param getVariables - a fn that returns something serializable to send to the API
 */
export function useSnippets(
  getVariables: () => OperationVariables | undefined,
  onCompleted: (snippetList: FetchSnippetQueryResponse) => void = noOp,
  query: DocumentNode = fetchSnippetsQuery,
): UseSnippetsResponse {
  const {
    data: snippetQueryData,
    error: hasError,
    errorMessage,
    isLoading,
    // previousData,
    refetch,
  } = useGraphql(query, {
    notifyOnNetworkStatusChange: true,
    variables: getVariables(),
    onCompleted,
  });

  // protect against the undefined response while the query is running
  if (isDefined<FetchSnippetQueryResponse>(snippetQueryData)) {
    const { paginationInfo, results } = snippetQueryData.fetchSnippets || {};
    return {
      errorMessage,
      hasError,
      isLoading,
      paginationInfo,
      refetch,
      results,
    };
  }

  return {
    errorMessage,
    hasError: false,
    isLoading,
    paginationInfo: {} as GraphQLResponsePaginationInfo,
    refetch: noOp,
    results: [],
  };
}
